/**
 * Utility functions for parsing chat messages from the Cosimo agent.
 * Extracts and converts CosimoAgentResponse data into UI-friendly formats.
 */

import {
  type CosimoAgentResponse,
  type SearchWithRecommendations,
  parseCosimoAgentResponse,
} from "@/lib/shop-agent-client";

export interface SearchTool {
  searchId?: string;
  label: string;
  searchText: string;
}

export interface BrandResult {
  brand_name: string;
  brand_slug?: string;
  brand_description?: string | null;
  domain?: string | null;
  justification?: string;
}

export interface ParsedAssistantContent {
  content: string;
  searchTools: SearchTool[];
  brandResults: BrandResult[];
}

/**
 * ChatMessage interface shared across chat-related modules.
 * Represents a single message in a chat conversation.
 */
export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  isStreaming?: boolean;
  timestamp?: string;
  searchTools?: SearchTool[];
  brandResults?: BrandResult[];
  /** OpenTelemetry run ID for trace linking (debug mode) */
  runId?: string;
  /** LangSmith project name for trace URL construction (debug mode) */
  langsmithProject?: string;
  /** LangSmith workspace ID for trace URL construction (debug mode) */
  langsmithWorkspaceId?: string;
  /** LangSmith project ID (UUID) for trace URL construction (debug mode) */
  langsmithProjectId?: string;
}

/**
 * Converts a SearchWithRecommendations object to a SearchTool.
 */
export function searchToSearchTool(search: SearchWithRecommendations): SearchTool {
  return {
    searchId: search.search_id,
    label: search.search_label || "Search Results",
    searchText: search.search_label || "Search Results",
  };
}

/**
 * Extracts SearchTool array from CosimoAgentResponse searches.
 */
export function extractSearchTools(response: CosimoAgentResponse): SearchTool[] {
  if (!response.searches || !Array.isArray(response.searches)) {
    return [];
  }
  return response.searches.map(searchToSearchTool);
}

/**
 * Extracts BrandResult array from CosimoAgentResponse brands.
 */
export function extractBrandResults(response: CosimoAgentResponse): BrandResult[] {
  if (!response.brands || !Array.isArray(response.brands)) {
    return [];
  }
  return response.brands.map((brand) => ({
    brand_name: brand.brand_name,
    brand_slug:
      "brand_slug" in brand && typeof brand.brand_slug === "string" ? brand.brand_slug : undefined,
    brand_description: brand.brand_description,
    justification: brand.justification,
  }));
}

/**
 * Parses raw assistant message content string into structured data.
 * Returns null if parsing fails completely.
 */
export function parseAssistantContent(rawContent: string): ParsedAssistantContent | null {
  const parsed = parseCosimoAgentResponse(rawContent);

  if (!parsed) {
    return null;
  }

  return {
    content: parsed.message || "",
    searchTools: extractSearchTools(parsed),
    brandResults: extractBrandResults(parsed),
  };
}

/**
 * Updates existing search tools with refined labels from a new response.
 * Preserves existing tools but updates their labels if the response contains matching searches.
 */
export function updateSearchToolsWithLabels(
  existingTools: SearchTool[],
  response: CosimoAgentResponse
): SearchTool[] {
  if (!response.searches || !Array.isArray(response.searches)) {
    return existingTools;
  }

  return existingTools.map((tool) => {
    const matchingSearch = response.searches?.find(
      (s: SearchWithRecommendations) => s.search_id === tool.searchId
    );

    if (matchingSearch?.search_label) {
      return {
        searchId: tool.searchId,
        label: matchingSearch.search_label,
        searchText: matchingSearch.search_label,
      };
    }

    return {
      searchId: tool.searchId,
      label: tool.label,
      searchText: tool.searchText,
    };
  });
}
